import React from 'react';
import 'moment/locale/pt-br';
import i18next from 'i18next';
import Strong from '~/components/Strong';
import { formatPrice } from '~/Utils/format';
import { InputNumber } from 'formik-antd';
import FormControl from '~/components/Form/FormControl';
import { SelectAntdFormik } from '~/components/Pagination/styles';
import { Flex } from 'antd';
import Button from '~/components/Button';
import { FaTrash } from 'react-icons/fa';

export const swoiColumns = (setFieldValue, isMobile) => {
  const calcTotals = (record, value, index) => {
    const newValue = Number(value);
    const selling = record.consigned_amount - newValue;
    const totalCostPrice = record.item_cost * selling;
    const totalSellPrice = record.price_unit * selling;

    record = {
      ...record,
      amount: selling,
      amount_returned: newValue,
      total_cost_price: totalCostPrice,
      total_consigned_price: totalSellPrice,
    };

    setFieldValue(`stock_write_offs.swoi.${index}`, record);
  };

  if (isMobile) {
    return [
      {
        title: i18next.t('screens:default.summary'),
        dataIndex: 'summary',
        key: 'summary',
        render: (_, record, index) => (
          <div>
            <p>
              <Strong>{i18next.t('screens:Nome')}:</Strong> {record.item_name}
            </p>
            <p>
              <Strong>{i18next.t('Qtde consignado')}:</Strong> {record.consigned_amount}
            </p>
            <p>
              <Strong>{i18next.t('Qtde devolução')}:</Strong>
              <InputNumber
                type="number"
                style={{ width: '100%' }}
                max={record.consigned_amount}
                value={record?.amount_returned || 0}
                onChange={(value) => calcTotals(record, value, index)}
              />
            </p>
            <p>
              <Strong>{i18next.t('Qtde venda')}:</Strong> {record.amount}
            </p>
            <p>
              <Strong>{i18next.t('Valor de custo (R$)')}:</Strong>{' '}
              {parseFloat(record.item_cost).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p>
              <Strong>{i18next.t('Total de custo (R$)')}:</Strong>{' '}
              {parseFloat(record.total_cost_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p>
              <Strong>{i18next.t('Valor de venda (R$)')}:</Strong>{' '}
              {parseFloat(record.price_unit).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p>
              <Strong>{i18next.t('Total de venda (R$)')}:</Strong>{' '}
              {parseFloat(record.total_consigned_price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </div>
        ),
      },
    ];
  }

  return [
    {
      title: i18next.t('screens:Nome'),
      dataIndex: 'item_name',
      key: 'item_name',
    },
    {
      title: i18next.t('Qtde consignado'),
      dataIndex: 'consigned_amount',
      key: 'consigned_amount',
    },
    {
      title: i18next.t('Qtde devolução'),
      dataIndex: 'amount_returned',
      key: 'amount_returned',
      render: (_, record, index) => {
        return (
          <InputNumber
            type="number"
            style={{ width: '100%' }}
            max={record.consigned_amount}
            value={record?.amount_returned || 0}
            onChange={(value) => {
              calcTotals(record, value, index);
            }}
          />
        );
      },
    },
    {
      title: i18next.t('Qtde venda'),
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: i18next.t('Valor de custo'),
      children: [
        {
          title: i18next.t('Unitário (R$)'),
          dataIndex: 'item_cost',
          key: 'item_cost',
          render: (text) => {
            const parsedValue = parseFloat(text);
            return parsedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          },
        },
        {
          title: i18next.t('Total (R$)'),
          dataIndex: 'cost_total',
          key: 'cost_total',
          render: (_, record) => {
            const totalCost = record.item_cost * record?.amount || 0;

            if (record) {
              record.total_cost_price = totalCost;
            }

            return formatPrice(totalCost);
          },
        },
      ],
    },
    {
      title: i18next.t('Valor de venda'),
      children: [
        {
          title: i18next.t('Unitário (R$)'),
          dataIndex: 'price_unit',
          key: 'price_unit',
          render: (text) => {
            const parsedValue = parseFloat(text);
            return parsedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          },
        },
        {
          title: i18next.t('Total (R$)'),
          dataIndex: 'price_total',
          key: 'price_total',
          render: (_, record) => {
            const quantidadeVendida = record?.amount || 0;
            const formattedPrice = parseFloat(
              record.price_unit
                ?.replace(/[^\d]/g, '') // Remove todos os caracteres não numéricos
                .replace(/(\d{2})$/, '.$1') // Adiciona o ponto antes das últimas 2 casas
            );
            const total = formattedPrice * quantidadeVendida;

            if (record) {
              record.total_consigned_price = total;
            }

            return total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          },
        },
      ],
    },
  ];
};

export const stockWriteOffItemsColumns = (fetchedItems, errors, fluidPermissions, setFieldValue) => {
  return [
    {
      title: i18next.t('fields:stockWriteOffs.typeProduct'),
      dataIndex: 'item_id',
      key: 'item_id',
      width: '60%',
      render: (_, record, index) => (
        <FormControl
          field={`stock_write_off_items.${index}.item_id`}
          error={errors?.stock_write_off_items?.[index]?.item_id}
          list
        >
          <SelectAntdFormik
            placeholder={i18next.t('fields:stockWriteOffs.selectProduct')}
            name={`stock_write_off_items.${index}.item_id`}
            onSelect={(value, options) => {
              setFieldValue(`stock_write_off_items.${index}.item_name`, options.children);
            }}
            disabled={!fluidPermissions()}
          >
            <SelectAntdFormik.Option value="">{i18next.t('screens:default.select')}</SelectAntdFormik.Option>
            {fetchedItems?.map((item) => (
              <SelectAntdFormik.Option key={item.id} value={item.id}>
                <p style={{ color: item.deleted_at ? 'red' : '', fontWeight: 'bold' }}>{item.name}</p>
              </SelectAntdFormik.Option>
            ))}
          </SelectAntdFormik>
        </FormControl>
      ),
    },
    {
      title: i18next.t('fields:stockWriteOffs.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: '40%',
      render: (_, record, index) => (
        <FormControl
          field={`stock_write_off_items.${index}.amount`}
          error={errors?.stock_write_off_items?.[index]?.amount}
          list
        >
          <InputNumber name={`stock_write_off_items.${index}.amount`} disabled={!fluidPermissions()} />
        </FormControl>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record, index) => (
        <Flex align="center" justify="center">
          <Button
            color="danger"
            title="Remover item"
            size="small"
            style={{ width: '30px' }}
            onClick={() => record.remove(index)}
            disabled={!fluidPermissions()}
            icon={<FaTrash />}
          />
        </Flex>
      ),
    },
  ];
};
